import React from "react";
import clsx from "clsx";
import moment from "moment";
import { toast } from "react-toastify";
import { MI } from "./icons";
import { useStyles } from "./styles";
import { COLORS, TONE_SOFT, TONE } from "./theme";
import { ANCHORS, STATUS } from "./mockData";
import { ChecklistItem, Comment, Role, Row, StatusKey } from "./types";
import { AgendaController } from "./useAgendaState";
import { useFetchContext } from "../../../common/FetchContext";
import { useHistory } from "react-router-dom";
import { CircularProgress } from "@material-ui/core";
import { useTipTapManualSave } from "../../projectManagementMutations";
import { urls } from "../../../common/urls";

interface Props {
  ctl: AgendaController;
  eNum: (r: Row) => string;
  openRow: Row;
}

/* ────────────────────────────── writer, still writing ─────────────────────────── */

function PanelWriting({ onSubmit }: { onSubmit: () => void }) {
  const c = useStyles();
  return (
    <>
      <div className={c.panelHead}>
        <div className="row">
          <h3>Report</h3>
        </div>
        <div className="sub">
          Nothing has come back yet. Submit when the report is ready for review.
        </div>
      </div>
      <div className={c.panelBody}>
        <div className={c.empty} style={{ padding: "40px 10px" }}>
          <MI name="pencil" size={26} color={COLORS.ink3} />
          <p>You are writing this report</p>
          <span>The reviewer's checklist appears here once it comes back.</span>
        </div>
      </div>
      <div className={c.panelFoot}>
        <div className="msg">
          <MI name="infoCircle" size={14} />
          Submitting hands the report to the reviewer and it leaves your grid
          until they respond.
        </div>
        <button
          className={clsx(c.act, c.actPrimary)}
          style={{ width: "100%", justifyContent: "center" }}
          onClick={onSubmit}
        >
          <MI name="send" size={14} />
          Submit for review
        </button>
      </div>
    </>
  );
}

/* ────────────────────────────── checklist (reviewer / writer) ─────────────────── */

function NoteEditor({
  item,
  ctl,
}: {
  item: ChecklistItem;
  ctl: AgendaController;
}) {
  const c = useStyles();
  const [text, setText] = React.useState(item.note || "");
  const showErr = ctl.state.editErr === item.id;
  return (
    <div className={c.editor}>
      <textarea
        rows={3}
        placeholder="What is wrong, and what should the writer change?"
        value={text}
        onChange={(e) => setText(e.target.value)}
      />
      {showErr && (
        <div className={c.err}>
          <MI name="alertCircle" size={14} />
          Write the note first
        </div>
      )}
      <div className="row">
        <span className={c.hint}>
          {ctl.me()} · {moment().format("DD-MM-YYYY HH:mm")}
        </span>
        <span className="sp" />
        <button
          className={clsx(c.mini, c.miniNo)}
          onClick={() => ctl.dispatch({ type: "CANCEL_NOTE" })}
        >
          <MI name="x" size={13} />
          Cancel
        </button>
        <button
          className={clsx(c.mini, c.miniGo)}
          onClick={() => {
            const v = text.trim();
            if (!v) {
              ctl.dispatch({ type: "SET_NOTE_ERR", id: item.id });
              return;
            }
            ctl.dispatch({ type: "SAVE_NOTE", id: item.id, note: v });
          }}
        >
          <MI name="check" size={13} />
          Save note
        </button>
      </div>
    </div>
  );
}

function CkItemRow({
  item,
  editable,
  ctl,
}: {
  item: ChecklistItem;
  editable: boolean;
  ctl: AgendaController;
}) {
  const c = useStyles();
  const needsNote = item.s === "p" && !item.note;

  const glyph =
    item.s === "d" ? (
      <MI name="check" size={12} />
    ) : item.s === "p" ? (
      <MI name="clock" size={12} />
    ) : null;

  const box = editable ? (
    <button
      className={clsx(
        c.cbox,
        c.cboxBtn,
        item.s === "d" && c.cboxD,
        item.s === "p" && c.cboxP,
        item.s === "n" && c.cboxN,
      )}
      onClick={() => ctl.dispatch({ type: "TOGGLE_DONE_ITEM", id: item.id })}
      title={item.s === "d" ? "Uncheck" : "Mark done"}
    >
      {glyph}
    </button>
  ) : (
    <span
      className={clsx(
        c.cbox,
        item.s === "d" && c.cboxD,
        item.s === "p" && c.cboxP,
        item.s === "n" && c.cboxN,
      )}
    >
      {glyph}
    </span>
  );

  // DISABLE LOGIC:
  // Disable if checkbox is marked true ('d') OR if a note is already saved
  const isFlagDisabled = item.s === "d" || (item.s === "p" && !!item.note);

  const tools = editable && (
    <div className={c.itemTools}>
      <button
        className={clsx(c.flagbtn, item.s === "p" && c.flagbtnOn)}
        disabled={isFlagDisabled}
        style={isFlagDisabled ? { opacity: 0.4, cursor: "not-allowed" } : {}}
        onClick={() => {
          if (isFlagDisabled) return; // Safety check to prevent clicks when disabled

          ctl.dispatch({ type: "TOGGLE_PENDING", id: item.id });
          if (item.s !== "p") {
            ctl.dispatch({ type: "EDIT_NOTE", id: item.id });
          } else if (ctl.state.editing === item.id) {
            ctl.dispatch({ type: "EDIT_NOTE", id: null });
          }
        }}
        title={item.s === "p" ? "Clear pending" : "Flag as pending"}
      >
        <MI name="clock" size={15} />
      </button>
    </div>
  );

  return (
    <div className={clsx(c.citem, needsNote && c.citemFlag)}>
      {box}
      <div className={c.ctext}>
        {item.t}
        {item.note && (
          <div className={c.cnote}>
            {item.note}
            <span className="w">
              jennifer.bravo · {moment().format("DD-MM-YYYY HH:mm")}
            </span>
          </div>
        )}
        {needsNote && ctl.state.editing !== item.id && (
          <div className={c.err}>
            <MI name="alertCircle" size={14} />
            Pending items need a note
          </div>
        )}
        {ctl.state.editing === item.id && <NoteEditor item={item} ctl={ctl} />}
      </div>
      {tools}
    </div>
  );
}

function WriterCkCard({
  item,
  ctl,
  onAcknowledge,
}: {
  item: ChecklistItem;
  ctl: AgendaController;
  onAcknowledge: (id: string) => Promise<void>;
}) {
  const c = useStyles();

  const notes = Array.isArray(item.notes) ? item.notes : [];

  return (
    <div className={clsx(c.cm, item.ack ? c.cmFixed : c.cmHeld)}>
      <div className={c.cmTop}>
        <span className={c.cmWho}>
          <MI name="checklist" size={15} />
          {item.t}
        </span>

        {item.ack && (
          <span className={clsx(c.tag, c.tagFixed)}>
            <MI name="check" size={12} />
            Done
          </span>
        )}
      </div>

      <div className={c.cmBody}>
        {notes.length > 0 ? (
          notes.map((note) => (
            <div
              key={note.id}
              style={{
                marginBottom: 10,
                paddingBottom: 10,
                borderBottom: "1px solid #eee",
              }}
            >
              <div style={{ marginBottom: 4 }}>{note.comment}</div>

              {note.is_acknowledged && (
                <div
                  style={{
                    fontSize: 11,
                    color: COLORS.ink3,
                  }}
                >
                  Acknowledged
                </div>
              )}
            </div>
          ))
        ) : (
          <span>{item.note || ""}</span>
        )}
      </div>

      <div className={c.cmFoot}>
        {item.ack ? (
          <span className={c.ackdone}>
            <MI name="circleCheck" size={14} />
            Acknowledged · {ctl.me()}
          </span>
        ) : (
          <button
            className={c.ackbtn}
            onClick={async () => {
              try {
                await onAcknowledge(item.id);

                ctl.dispatch({
                  type: "ACK_ITEM",
                  id: item.id,
                });

                toast.success("Acknowledged");
              } catch (error) {
                toast.error("Failed to acknowledge checklist item");
              }
            }}
          >
            <MI name="check" size={14} />
            I've fixed this
          </button>
        )}
      </div>
    </div>
  );
}

function PanelChecklist({
  ctl,
  r,
  editable,
  writerMode,
  onSendBack,
  onPassOn,
  onReturnTo,
  checklistData,
  isLoading,
}: {
  ctl: AgendaController;
  r: Row;
  editable: boolean;
  writerMode: boolean;
  onSendBack: () => void;
  onPassOn: () => void;
  onReturnTo: () => void;
  checklistData?: any;
  isLoading?: boolean;
}) {
  const c = useStyles();
  const [localOverrides, setLocalOverrides] = React.useState<
    Record<string, any>
  >({});

  // 1. Initialize authAxios here
  const { authAxios } = useFetchContext();

  const apiResult = React.useMemo(() => {
    if (!checklistData) return null;

    return (
      checklistData?.data?.results?.[0] ||
      checklistData?.results?.[0] ||
      checklistData?.[0] ||
      checklistData ||
      null
    );
  }, [checklistData]);

  const rawApiList = React.useMemo(() => {
    return apiResult?.check_list || null;
  }, [apiResult]);

  // 2. Updated API call using authAxios
  const handleApiUpdate = async (
    checkpointId: string,
    apiStatus: boolean | null,
  ) => {
    const categoryId =
      checklistData?.data?.results?.[0]?.category_id ??
      checklistData?.results?.[0]?.category_id ??
      checklistData?.[0]?.category_id ??
      checklistData?.category_id ??
      null;

    const payload = {
      report_result_id: r.id,
      category_id: categoryId,
      checklist_data: [
        {
          id: Number(checkpointId),
          status: apiStatus,
        },
      ],
    };

    try {
      // Replaced fetch with authAxios.post
      await authAxios.post(urls.reportWriterAgenda.addCheckList, payload);
    } catch (error) {
      console.error("Failed to update checklist status on server:", error);
      toast.error("Failed to save checkbox status to the server.");
    }
  };

  const handleSaveNoteApi = async (
    checklistId: string,
    commentText: string,
  ) => {
    const categoryId =
      checklistData?.data?.results?.[0]?.category_id ??
      checklistData?.results?.[0]?.category_id ??
      checklistData?.[0]?.category_id ??
      checklistData?.category_id ??
      null;

    const payload = {
      report_result_id: r.id,
      comment: commentText,
      checklist_id: Number(checklistId),
      category_id: categoryId,
    };

    try {
      await authAxios.post(
        urls.reportWriterAgenda.Check_List_notes_customCreate,
        payload,
      );
    } catch (error) {
      console.error("Failed to save note on server:", error);
      toast.error("Failed to save the note to the server.");
    }
  };

  const acknowledgeChecklistItem = async (
    checklistId: string,
  ): Promise<void> => {
    const categoryId =
      checklistData?.data?.results?.[0]?.category_id ??
      checklistData?.results?.[0]?.category_id ??
      checklistData?.[0]?.category_id ??
      checklistData?.category_id ??
      null;

    const payload = {
      report_result_id: r.id,
      checklist_id: Number(checklistId),
      category_id: categoryId,
    };

    try {
      await authAxios.post(
        urls.reportWriterAgenda.Check_List_notes_acknowledged,
        payload,
      );
    } catch (error) {
      console.error("Failed to acknowledge checklist item:", error);
      throw error;
    }
  };
  const activeChecklist = React.useMemo(() => {
    if (rawApiList && Array.isArray(rawApiList)) {
      return rawApiList.map((group: any) => ({
        g: group.category || "Checklist",
        gi: "list",

        items: Array.isArray(group.check_point)
          ? group.check_point.map((cp: any) => {
              const id = String(cp.id);
              const override = localOverrides[id] || {};

              const baseStatus =
                cp.status === true ? "d" : cp.status === false ? "p" : "n";

              // NEW API FORMAT
              const notes = Array.isArray(cp.notes) ? cp.notes : [];

              // Use the first note as the main note
              const note =
                override.note !== undefined
                  ? override.note
                  : notes[0]?.comment || "";

              // Item is acknowledged when any API note is acknowledged
              const acknowledged =
                override.ack !== undefined
                  ? override.ack
                  : notes.some((n: any) => n.is_acknowledged === true);

              return {
                id,
                t: cp.description || "",
                s: override.s !== undefined ? override.s : baseStatus,

                note,
                ack: acknowledged,

                // Keep normalized notes for WriterCkCard
                notes,

                note_count: cp.note_count || notes.length,

                category: group.category,
              };
            })
          : [],
      }));
    }

    return ctl.state.checklist;
  }, [rawApiList, ctl.state.checklist, localOverrides]);

  const proxyCtl = React.useMemo(() => {
    return {
      ...ctl,
      dispatch: (action: any) => {
        const item = activeChecklist
          .flatMap((g: any) => g.items)
          .find((i: any) => i.id === action.id);

        if (action.type === "TOGGLE_DONE_ITEM" && item) {
          const isCurrentlyDone = item.s === "d";
          const newInternalStatus = isCurrentlyDone ? "n" : "d";
          const newApiStatus = isCurrentlyDone ? null : true;

          setLocalOverrides((prev) => ({
            ...prev,
            [action.id]: { ...prev[action.id], s: newInternalStatus },
          }));

          handleApiUpdate(action.id, newApiStatus);
        } else if (action.type === "TOGGLE_PENDING" && item) {
          const isCurrentlyPending = item.s === "p";
          const newInternalStatus = isCurrentlyPending ? "n" : "p";
          const newApiStatus = isCurrentlyPending ? null : false;

          setLocalOverrides((prev) => ({
            ...prev,
            [action.id]: { ...prev[action.id], s: newInternalStatus },
          }));

          handleApiUpdate(action.id, newApiStatus);
        } else if (action.type === "SAVE_NOTE") {
          // 1. Save the note locally and ensure the internal status is set to pending ("p")
          setLocalOverrides((prev) => ({
            ...prev,
            [action.id]: { ...prev[action.id], note: action.note, s: "p" },
          }));

          // 2. Call the API setting the checkbox status to false
          handleApiUpdate(action.id, false);

          // 3. Call the API to save the actual note text
          handleSaveNoteApi(action.id, action.note);

          // 4. Close the note editor
          ctl.dispatch({ type: "EDIT_NOTE", id: null });
        } else if (action.type === "ACK_ITEM") {
          setLocalOverrides((prev) => ({
            ...prev,
            [action.id]: { ...prev[action.id], ack: true },
          }));
        } else {
          // Forward all other actions (like opening the editor or cancelling) to the original controller
          ctl.dispatch(action);
        }
      },
    };
  }, [ctl, activeChecklist, authAxios]);

  let total = 0,
    done = 0,
    pend = 0,
    acked = 0,
    missing: any[] = [];

  activeChecklist.forEach((g: any) => {
    g.items.forEach((i: any) => {
      total++;
      if (i.s === "d") done++;
      if (i.s === "p") pend++;
      if (i.ack) acked++;
      if (i.s === "p" && !i.note) missing.push(i);
    });
  });

  const unchecked = total - done - pend;

  return (
    <>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          height: "100vh",
          overflow: "hidden",
        }}
      >
        <div className={c.panelHead} style={{ flexShrink: 0 }}>
          <div className="row">
            <h3>Checklist</h3>
            <span style={{ flex: 1 }} />
            {writerMode ? (
              <span
                className={clsx(
                  c.tag,
                  acked === pend ? c.tagFixed : c.tagNotsent,
                )}
              >
                {acked} of {pend} done
              </span>
            ) : (
              <span
                style={{
                  fontFamily: "'DM Mono', monospace",
                  fontSize: 12,
                  color: COLORS.ink3,
                }}
              >
                {done}/{total} checked
              </span>
            )}
          </div>
          <div className="sub">
            {editable
              ? "Tick what passes. Flag what does not and write a note — the notes are what the writer receives."
              : writerMode
                ? "Only the items the reviewer flagged. Fix each one in the report, then acknowledge it."
                : "Read-only."}
          </div>
        </div>
        <div className={c.panelBody} style={{ flexGrow: 1, overflowY: "auto" }}>
          {isLoading && (
            <div style={{ padding: 12, textAlign: "center", color: "#888" }}>
              Loading checklist...
            </div>
          )}
          {editable && !isLoading && (
            <div className={c.legend}>
              <span>
                <i className={clsx(c.lg, c.lgD)}>
                  <MI name="check" size={9} />
                </i>
                Done {done}
              </span>
              <span>
                <i className={clsx(c.lg, c.lgP)}>
                  <MI name="clock" size={9} />
                </i>
                Pending {pend}
              </span>
              <span>
                <i className={clsx(c.lg, c.lgN)} />
                Not checked {unchecked}
              </span>
            </div>
          )}
          {!isLoading && writerMode ? (
            pend > 0 ? (
              activeChecklist.map((g: any) => {
                const pendingItems = g.items.filter((i: any) => i.s === "p");

                if (!pendingItems.length) return null;

                return (
                  <React.Fragment key={g.g}>
                    <div className={c.cgroup}>
                      <MI name={g.gi} size={14} />
                      {g.g}
                    </div>

                    {pendingItems.map((i: any) => (
                      <WriterCkCard
                        key={i.id}
                        item={i}
                        ctl={proxyCtl}
                        onAcknowledge={acknowledgeChecklistItem}
                      />
                    ))}
                  </React.Fragment>
                );
              })
            ) : (
              <div className={c.empty} style={{ padding: "36px 10px" }}>
                <MI name="circleCheck" size={26} color={COLORS.ink3} />
                <p>Nothing flagged</p>
              </div>
            )
          ) : (
            !isLoading &&
            activeChecklist.map((g: any) => (
              <React.Fragment key={g.g}>
                <div className={c.cgroup}>
                  <MI name={g.gi} size={14} />
                  {g.g}
                </div>
                {g.items.map((i: any) => (
                  <CkItemRow
                    key={i.id}
                    item={i}
                    editable={editable}
                    ctl={proxyCtl}
                  />
                ))}
              </React.Fragment>
            ))
          )}
        </div>
        <div className={c.panelFoot} style={{ flexShrink: 0 }}>
          {editable ? (
            missing.length ? (
              <>
                <div className="msg block">
                  <MI name="alertTriangle" size={14} />
                  {missing.length} pending item
                  {missing.length > 1 ? "s" : ""} still need a note.
                </div>
                <button
                  className={clsx(c.act, c.actDisabled)}
                  style={{ width: "100%", justifyContent: "center" }}
                  disabled
                >
                  <MI name="back" size={14} />
                  Send back to writer
                </button>
              </>
            ) : unchecked ? (
              <>
                <div className="msg">
                  <MI name="deviceFloppy" size={14} />
                  <span>
                    <b>
                      {done + pend} of {total} checked
                    </b>{" "}
                    — {unchecked} still to go. Your progress is saved; close
                    this and come back whenever.
                  </span>
                </div>
                <button
                  className={clsx(c.act, c.actDisabled)}
                  style={{ width: "100%", justifyContent: "center" }}
                  disabled
                >
                  <MI name="back" size={14} />
                  Send back to writer
                </button>
                <div className="msg">
                  <MI name="infoCircle" size={14} />
                  Finish every item before sending back or passing on.
                </div>
              </>
            ) : pend ? (
              <>
                <div className="msg">
                  <MI name="eyeOff" size={14} />
                  <span>
                    {pend} pending item{pend > 1 ? "s" : ""} —{" "}
                    <b>not sent yet</b>. {r.writer} sees nothing until you send.
                  </span>
                </div>
                <button
                  className={clsx(c.act, c.actWarn)}
                  style={{ width: "100%", justifyContent: "center" }}
                  onClick={onSendBack}
                >
                  <MI name="back" size={14} />
                  Send back to writer · {pend}
                </button>
              </>
            ) : (
              <>
                <div className="msg">
                  <MI name="circleCheck" size={14} />
                  All {total} items checked and passing — nothing to send back.
                </div>
                <button
                  className={clsx(c.act, c.actGo)}
                  style={{ width: "100%", justifyContent: "center" }}
                  onClick={onPassOn}
                >
                  <MI name="arrowRight" size={14} />
                  Pass to verifier
                </button>
              </>
            )
          ) : writerMode ? (
            <>
              <div className={clsx("msg", acked === pend ? "" : "block")}>
                <MI
                  name={acked === pend ? "circleCheck" : "alertCircle"}
                  size={14}
                />
                {acked === pend
                  ? "All items acknowledged."
                  : `Acknowledge all ${pend} items before sending back.`}
              </div>
              <button
                className={clsx(
                  c.act,
                  acked === pend ? c.actPrimary : c.actDisabled,
                )}
                style={{ width: "100%", justifyContent: "center" }}
                disabled={acked !== pend}
                onClick={onReturnTo}
              >
                <MI name="send" size={14} />
                Send back to reviewer
              </button>
            </>
          ) : (
            <>
              <div className="msg">
                <MI name="user" size={14} />
                {acked} of {pend} fixed by {r.writer}.
              </div>
              <button
                className={clsx(c.act, c.actDisabled)}
                style={{ width: "100%", justifyContent: "center" }}
                disabled
              >
                <MI name="clock" size={14} />
                Waiting on writer
              </button>
            </>
          )}
        </div>
      </div>
    </>
  );
}

/* ────────────────────────────── comments panel ────────────────────────────────── */

function CmCard({
  c: cm,
  mode,
  ctl,
}: {
  c: Comment;
  mode: "gate" | "writer" | "copy";
  ctl: AgendaController;
}) {
  const c = useStyles();
  const names = { verify: "Verification", approval: "Approval" };
  const active = ctl.state.activeAnchor === cm.anchor;
  return (
    <div
      className={clsx(
        c.cm,
        cm.held && c.cmHeld,
        cm.ack && c.cmFixed,
        active && c.cmActive,
      )}
      onClick={() => ctl.dispatch({ type: "SET_ANCHOR", anchor: cm.anchor })}
    >
      <div className={c.cmTop}>
        <span className={c.cmWho}>
          <MI name="userCircle" size={15} />
          {cm.by}
        </span>
        <span
          className={clsx(
            c.tag,
            cm.stage === "verify" ? c.tagVerify : c.tagApproval,
          )}
        >
          <MI name={cm.stage === "verify" ? "verify" : "stamp"} size={12} />
          {names[cm.stage]}
        </span>
        {cm.held && (
          <span className={clsx(c.tag, c.tagNotsent)}>
            <MI name="eyeOff" size={12} />
            Not sent
          </span>
        )}
        {cm.ack && (
          <span className={clsx(c.tag, c.tagFixed)}>
            <MI name="check" size={12} />
            Fixed
          </span>
        )}
        <span className={c.cmWhen}>{cm.at}</span>
      </div>
      <div className={c.cmAnchor}>
        <MI name="link" size={12} />
        {cm.anchor}
      </div>
      <div className={c.cmBody}>{cm.text}</div>
      {mode === "writer" && (
        <div className={c.cmFoot}>
          {cm.ack ? (
            <span className={c.ackdone}>
              <MI name="circleCheck" size={14} />
              Acknowledged
            </span>
          ) : (
            <button
              className={c.ackbtn}
              onClick={async (e) => {
                e.stopPropagation();

                try {
                  // await ctl.acknowledgeComment(cm.id);

                  ctl.dispatch({
                    type: "ACK_COMMENT",
                    id: cm.id,
                  });

                  toast.success("Acknowledged");
                } catch (error) {
                  toast.error("Failed to acknowledge comment");
                }
              }}
            >
              <MI name="check" size={14} />
              I've fixed this
            </button>
          )}
        </div>
      )}
      {mode === "gate" && cm.ack && (
        <div className={c.cmFoot}>
          <span className={c.ackdone}>
            <MI name="userCheck" size={14} />
            Writer marked this fixed
          </span>
        </div>
      )}
    </div>
  );
}

function Composer({
  ctl,
  stage,
}: {
  ctl: AgendaController;
  stage: "verify" | "approval";
}) {
  const c = useStyles();
  const [anchor, setAnchor] = React.useState(
    ctl.state.activeAnchor || ANCHORS[0],
  );
  const [text, setText] = React.useState("");
  const [err, setErr] = React.useState<string | null>(null);
  return (
    <div className={clsx(c.cm, c.cmActive)} style={{ cursor: "default" }}>
      <div className={c.cmTop}>
        <span className={c.cmWho}>
          <MI name="messagePlus" size={15} />
          New comment
        </span>
      </div>
      <select
        className={c.field}
        style={{ marginBottom: 8 }}
        value={anchor}
        onChange={(e) => setAnchor(e.target.value)}
      >
        {ANCHORS.map((a) => (
          <option key={a}>{a}</option>
        ))}
      </select>
      <textarea
        className={c.field}
        rows={3}
        placeholder="What needs fixing, and what should it say instead?"
        value={text}
        onChange={(e) => setText(e.target.value)}
        style={{ resize: "vertical" }}
      />
      {err && (
        <div className={c.err}>
          <MI name="alertCircle" size={14} />
          {err}
        </div>
      )}
      <div className={c.editor}>
        <div className="row">
          <span className={c.hint}>{ctl.me()} · held until you send</span>
          <span className="sp" />
          <button
            className={clsx(c.mini, c.miniNo)}
            onClick={() => ctl.dispatch({ type: "CANCEL_COMMENT" })}
          >
            <MI name="x" size={13} />
            Cancel
          </button>
          <button
            className={clsx(c.mini, c.miniGo)}
            onClick={() => {
              const t = text.trim();
              if (!t) {
                setErr("Write the comment first");
                return;
              }
              ctl.dispatch({
                type: "SAVE_COMMENT",
                stage,
                anchor,
                text: t,
                by: ctl.me(),
              });
              toast.success("Saved — not sent yet");
            }}
          >
            <MI name="check" size={13} />
            Save comment
          </button>
        </div>
      </div>
    </div>
  );
}

function PanelComments({
  ctl,
  r,
  stage,
  mode,
  comments,
  onSendBack,
  onPassOn,
  onApprove,
  onReturnTo,
}: {
  ctl: AgendaController;
  r: Row;
  stage: "verify" | "approval";
  mode: "gate" | "writer" | "copy";
  comments?: any[];
  onSendBack: () => void;
  onPassOn: () => void;
  onApprove: () => void;
  onReturnTo: () => void;
}) {
  const c = useStyles();
  const apiComments = React.useMemo(() => {
    if (!Array.isArray(comments)) return [];

    return comments.map((item: any) => ({
      id: item.id,
      text: item.comment || "",
      anchor: item.selected_text || "",
      by: item.comment_created_by || "",
      at: item.comment_created_on
        ? moment(item.comment_created_on).format("DD-MM-YYYY HH:mm")
        : "",
      stage,
      held: false,
      ack: false,
    }));
  }, [comments, stage]);

  const all = apiComments.length ? apiComments : ctl.cmFor(stage);
  const sent = ctl.cmSent(stage);
  const held = ctl.cmHeld(stage);
  const ack = ctl.cmAck(stage);

  return (
    <>
      <div className={c.panelHead}>
        <div className="row">
          <h3>Comments</h3>
          <span style={{ flex: 1 }} />
          {mode === "writer" ? (
            <span
              className={clsx(
                c.tag,
                ack.length === sent.length ? c.tagFixed : c.tagNotsent,
              )}
            >
              {ack.length} of {sent.length} done
            </span>
          ) : (
            <span
              style={{
                fontFamily: "'DM Mono', monospace",
                fontSize: 12,
                color: COLORS.ink3,
              }}
            >
              {all.length}
            </span>
          )}
        </div>
        <div className="sub">
          {mode === "gate"
            ? "Highlight text in the report to anchor a comment. Nothing reaches the writer until you send."
            : mode === "writer"
              ? "Fix each one in the report, then acknowledge it. The gate sees your progress."
              : "Read-only copy of the approver’s comments. No action needed from you."}
        </div>
      </div>
      <div className={c.panelBody}>
        {mode === "gate" && ctl.state.composing && (
          <Composer ctl={ctl} stage={stage} />
        )}
        {all.length ? (
          all.map((cm) => <CmCard key={cm.id} c={cm} mode={mode} ctl={ctl} />)
        ) : (
          <div className={c.empty} style={{ padding: "40px 10px" }}>
            <MI name="messageOff" size={26} color={COLORS.ink3} />
            <p>No comments yet</p>
            <span>
              {mode === "gate"
                ? "Highlight text in the report to add one."
                : "Nothing raised at this stage."}
            </span>
          </div>
        )}
      </div>
      <div className={c.panelFoot}>
        {mode === "gate" ? (
          held.length ? (
            <>
              <div className={c.sendbar}>
                <div className="txt">
                  <MI name="eyeOff" size={16} />
                  <span>
                    <b>
                      {held.length} comment{held.length > 1 ? "s" : ""} held.
                    </b>{" "}
                    {r.writer} cannot see {held.length > 1 ? "them" : "it"}{" "}
                    until you send.
                  </span>
                </div>
              </div>
              <button
                className={clsx(c.act, c.actWarn)}
                style={{ width: "100%", justifyContent: "center" }}
                onClick={onSendBack}
              >
                <MI name="back" size={14} />
                Send back to writer · {held.length}
              </button>
              {!ctl.state.composing && (
                <button
                  className={clsx(c.act, c.actSecondary)}
                  style={{ width: "100%", justifyContent: "center" }}
                  onClick={() => ctl.dispatch({ type: "START_COMMENT" })}
                >
                  <MI name="messagePlus" size={14} />
                  Add another comment
                </button>
              )}
            </>
          ) : (
            <>
              <div className="msg">
                <MI
                  name={
                    sent.length === 0 || ack.length === sent.length
                      ? "circleCheck"
                      : "clock"
                  }
                  size={14}
                />
                {sent.length
                  ? `${ack.length} of ${sent.length} fixed by ${r.writer}.`
                  : "No comments raised."}
              </div>
              {!ctl.state.composing && (
                <button
                  className={clsx(c.act, c.actSecondary)}
                  style={{ width: "100%", justifyContent: "center" }}
                  onClick={() => ctl.dispatch({ type: "START_COMMENT" })}
                >
                  <MI name="messagePlus" size={14} />
                  Add comment
                </button>
              )}
              {stage === "verify" ? (
                <button
                  className={clsx(
                    c.act,
                    sent.length === 0 || ack.length === sent.length
                      ? c.actGo
                      : c.actDisabled,
                  )}
                  disabled={!(sent.length === 0 || ack.length === sent.length)}
                  style={{ width: "100%", justifyContent: "center" }}
                  onClick={onPassOn}
                >
                  <MI name="arrowRight" size={14} />
                  Pass to approver
                </button>
              ) : (
                <button
                  className={clsx(
                    c.act,
                    sent.length === 0 || ack.length === sent.length
                      ? c.actGo
                      : c.actDisabled,
                  )}
                  disabled={!(sent.length === 0 || ack.length === sent.length)}
                  style={{ width: "100%", justifyContent: "center" }}
                  onClick={onApprove}
                >
                  <MI name="fileCheck" size={14} />
                  Approve and generate PDF
                </button>
              )}
            </>
          )
        ) : mode === "writer" ? (
          <>
            <div
              className={clsx(
                "msg",
                ack.length === sent.length && sent.length > 0 ? "" : "block",
              )}
            >
              <MI
                name={
                  ack.length === sent.length && sent.length > 0
                    ? "circleCheck"
                    : "alertCircle"
                }
                size={14}
              />
              {ack.length === sent.length && sent.length > 0
                ? "All comments acknowledged."
                : `Acknowledge all ${sent.length} comments before sending back.`}
            </div>
            <button
              className={clsx(
                c.act,
                ack.length === sent.length && sent.length > 0
                  ? c.actPrimary
                  : c.actDisabled,
              )}
              disabled={!(ack.length === sent.length && sent.length > 0)}
              style={{ width: "100%", justifyContent: "center" }}
              onClick={onReturnTo}
            >
              <MI name="send" size={14} />
              Send back to {stage === "verify" ? "verifier" : "approver"}
            </button>
          </>
        ) : (
          <div className="msg">
            <MI name="eye" size={14} />
            You were copied on these. They clear from your grid when the PDF is
            generated.
          </div>
        )}
      </div>
    </>
  );
}

/* ────────────────────────────── closed / approved ─────────────────────────────── */

function PanelClosed({ r }: { r: Row }) {
  const c = useStyles();
  return (
    <>
      <div className={c.panelHead}>
        <div className="row">
          <h3>Completed</h3>
          <span style={{ flex: 1 }} />
          <span className={clsx(c.tag, c.tagFixed)}>
            <MI name="check" size={12} />
            Closed
          </span>
        </div>
        <div className="sub">
          This report is approved and locked. Nothing can be changed.
        </div>
      </div>
      <div className={c.panelBody}>
        <div
          className={clsx(c.cm, c.cmFixed)}
          style={{ cursor: "default", marginTop: 12 }}
        >
          <div className={c.cmTop}>
            <span className={c.cmWho}>
              <MI name="userCheck" size={15} />
              {r.approvedBy}
            </span>
            <span className={c.cmWhen}>{r.doneAt}</span>
          </div>
          <div className={c.cmBody}>Approved and PDF generated.</div>
        </div>
        <div className={c.cm} style={{ cursor: "default" }}>
          <div className={c.cmTop}>
            <span className={c.cmWho}>
              <MI name="fileText" size={15} />
              {r.pdf}
            </span>
          </div>
          <div className={c.cmFoot}>
            <button
              className={c.ackbtn}
              onClick={() => toast.success(`Downloading ${r.pdf}`)}
            >
              <MI name="fileDownload" size={14} />
              Download PDF
            </button>
          </div>
        </div>
      </div>
      <div className={c.panelFoot}>
        <div className="msg">
          <MI name="lock" size={14} />
          Cleared from the reviewer, verifier and approver grids. Kept under
          Completed for the writer and super users.
        </div>
      </div>
    </>
  );
}

/* ────────────────────────────── side panel router ─────────────────────────────── */

/* ────────────────────────────── side panel router ─────────────────────────────── */

export function SidePanel({
  ctl,
  r,
  role,
  onTransition,
  checklistData,
  isLoading,
  fullTipTapJSON,
  reportID,
}: {
  ctl: AgendaController;
  r: Row;
  role: Role;
  onTransition: (kind: string) => void;
  checklistData?: any;
  isLoading?: boolean;
  fullTipTapJSON?: any;
  reportID?: any;
}) {
  const { authAxios } = useFetchContext();

  const history = useHistory();
  const c = useStyles();
  const { mutateAsync: ManualTipTapSave, isLoading: saveloading } =
    useTipTapManualSave();

  const MOVE_REPORT_URL = urls.docgen.moveReport;

  const moveReport = async (sentFrom: string, sentTo: string) => {
    const { data } = await authAxios.post(MOVE_REPORT_URL, {
      report_result_id: r.id,
      sent_from: sentFrom,
      sent_to: sentTo,
    });

    return data;
  };

  const handleMove = async (
    sentFrom: string,
    sentTo: string,
    transition: string,
  ) => {
    try {
      const saveType = sentFrom === "writer" ? "manual" : "autosave";
      // 1. Save the latest TipTap content first
      await ManualTipTapSave({
        authAxios,
        report_result_id: reportID,
        save_type: saveType,
        content: fullTipTapJSON,
      });

      // 2. Move the report only after save succeeds
      await moveReport(sentFrom, sentTo);

      // 3. Update side panel state
      onTransition(transition);

      toast.success("Report moved successfully");

      // 4. Redirect
      history.push("/reports/report_module");
    } catch (error) {
      console.error("Failed to save/move report:", error);
      toast.error("Failed to move report");
    }
  };

  if (r.st === "approved") return <PanelClosed r={r} />;

  if (role === "reviewer") {
    return (
      <PanelChecklist
        ctl={ctl}
        r={r}
        editable={true}
        writerMode={false}
        checklistData={checklistData}
        isLoading={isLoading}
        onSendBack={() => handleMove("reviewer", "writer", "sendBack:review")}
        onPassOn={() => handleMove("reviewer", "verifier", "passOn:verify")}
        onReturnTo={() => {}}
      />
    );
  }

  if (role === "writer") {
    // Reviewer sent checklist back to writer
    if (r.st === "corr_rev") {
      return (
        <PanelChecklist
          ctl={ctl}
          r={r}
          editable={false}
          writerMode={true}
          onSendBack={() => {}}
          onPassOn={() => {}}
          onReturnTo={() => handleMove("writer", "reviewer", "returnTo:review")}
          checklistData={checklistData}
          isLoading={isLoading}
        />
      );
    }

    // Verifier / Approver sent comments back to writer
    if (r.st === "corr_ver" || r.st === "corr_app") {
      const isVerifier = r.st === "corr_ver";

      return (
        <PanelComments
          ctl={ctl}
          r={r}
          stage={isVerifier ? "verify" : "approval"}
          mode="writer"
          onSendBack={() => {}}
          onPassOn={() => {}}
          onApprove={() => {}}
          onReturnTo={() =>
            handleMove(
              "writer",
              isVerifier ? "verifier" : "approver",
              isVerifier ? "returnTo:verify" : "returnTo:approval",
            )
          }
        />
      );
    }

    // Writer's normal report/checklist
    return (
      <PanelChecklist
        ctl={ctl}
        r={r}
        editable={true}
        writerMode={false}
        onSendBack={() => handleMove("writer", "reviewer", "returnTo:review")}
        onPassOn={() => {}}
        onReturnTo={() => {}}
        checklistData={checklistData}
        isLoading={isLoading}
      />
    );
  }

  if (role === "verifier") {
    if (["verify", "corr_ver"].includes(r.st)) {
      return (
        <PanelComments
          ctl={ctl}
          r={r}
          stage="verify"
          mode="gate"
          comments={
            r.st === "verify" && Array.isArray(checklistData)
              ? checklistData
              : undefined
          }
          onSendBack={() => handleMove("verifier", "writer", "sendBack:verify")}
          onPassOn={() => handleMove("verifier", "approver", "passOn:approval")}
          onApprove={() => {}}
          onReturnTo={() => {}}
        />
      );
    }

    return (
      <PanelComments
        ctl={ctl}
        r={r}
        stage="approval"
        mode="copy"
        onSendBack={() => {}}
        onPassOn={() => {}}
        onApprove={() => {}}
        onReturnTo={() => {}}
      />
    );
  }

  return (
    <PanelComments
      ctl={ctl}
      r={r}
      stage="approval"
      mode="gate"
      onSendBack={() => handleMove("approver", "writer", "sendBack:approval")}
      onPassOn={() => {}}
      onApprove={() => handleMove("approver", "completed", "approveAndPdf")}
      onReturnTo={() => {}}
    />
  );
}

/* ────────────────────────────── main report view ─────────────────────────────── */
